import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from '../hooks/useTranslation';
import { useCurrency } from '../hooks/useCurrency';

interface PartnerHotel {
    id: string;
    name: string;
    nameAr: string;
    nameTr: string;
    location: string;
    locationAr: string;
    locationTr: string;
    stars: number;
    category: 'luxury' | 'business' | 'resort' | 'boutique' | 'apartment';
    categoryAr: string;
    categoryTr: string;
    description: string;
    descriptionAr: string;
    descriptionTr: string;
    amenities: string[];
    amenitiesAr: string[];
    amenitiesTr: string[];
    priceFrom: number;
    image: string;
    gallery: string[];
    contact: {
        phone: string;
        email: string;
        website: string;
        address: string;
        addressAr: string;
        addressTr: string;
    };
    partnership: {
        discount: number;
        specialRates: boolean;
        exclusiveAccess: boolean;
    };
}

const PartnerHotels: React.FC = () => {
    const { t } = useTranslation();
    const { formatPrice } = useCurrency();
    const [hotels, setHotels] = useState<PartnerHotel[]>([]);
    const [filteredHotels, setFilteredHotels] = useState<PartnerHotel[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedLocation, setSelectedLocation] = useState('');
    const [selectedStars, setSelectedStars] = useState(0);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [sortBy, setSortBy] = useState('name');

    // Mock data based on the hotels from Google Drive
    const mockHotels: PartnerHotel[] = [
        {
            id: '1',
            name: 'Four Seasons Hotel Sultan Ahmet',
            nameAr: 'فندق فور سيزونز السلطان أحمد',
            nameTr: 'Four Seasons Hotel Sultan Ahmet',
            location: 'Istanbul, Sultanahmet',
            locationAr: 'إسطنبول، السلطان أحمد',
            locationTr: 'İstanbul, Sultanahmet',
            stars: 5,
            category: 'luxury',
            categoryAr: 'فاخر',
            categoryTr: 'Lüks',
            description: 'Luxury hotel in the heart of historic Istanbul with stunning views of Hagia Sophia and Blue Mosque.',
            descriptionAr: 'فندق فاخر في قلب إسطنبول التاريخية مع إطلالات خلابة على آيا صوفيا والمسجد الأزرق.',
            descriptionTr: 'Tarihi İstanbul\'un kalbinde, Ayasofya ve Sultanahmet Camii\'ne muhteşem manzaralı lüks otel.',
            amenities: ['WiFi', 'Spa', 'Restaurant', 'Concierge', 'Valet Parking'],
            amenitiesAr: ['واي فاي', 'سبا', 'مطعم', 'خدمة الكونسيرج', 'موقف سيارات'],
            amenitiesTr: ['WiFi', 'Spa', 'Restoran', 'Concierge', 'Valet Otopark'],
            priceFrom: 450,
            image: '/api/placeholder/400/300',
            gallery: ['/api/placeholder/400/300', '/api/placeholder/400/300'],
            contact: {
                phone: '+90 212 402 3000',
                email: 'istanbul@fourseasons.com',
                website: 'https://www.fourseasons.com/istanbul',
                address: 'Tevkifhane Sk. No:1, 34122 Fatih/İstanbul',
                addressAr: 'شارع توقيفخانه رقم 1، فاتح/إسطنبول',
                addressTr: 'Tevkifhane Sk. No:1, 34122 Fatih/İstanbul'
            },
            partnership: {
                discount: 15,
                specialRates: true,
                exclusiveAccess: true
            }
        },
        {
            id: '2',
            name: 'Raffles Istanbul',
            nameAr: 'فندق رافلز إسطنبول',
            nameTr: 'Raffles İstanbul',
            location: 'Istanbul, Beşiktaş',
            locationAr: 'إسطنبول، بشيكتاش',
            locationTr: 'İstanbul, Beşiktaş',
            stars: 5,
            category: 'luxury',
            categoryAr: 'فاخر',
            categoryTr: 'Lüks',
            description: 'Contemporary luxury hotel with panoramic Bosphorus views and world-class dining.',
            descriptionAr: 'فندق فاخر معاصر مع إطلالات بانورامية على البوسفور ومطاعم عالمية المستوى.',
            descriptionTr: 'Panoramik Boğaz manzarası ve dünya standartlarında yemekleri olan çağdaş lüks otel.',
            amenities: ['WiFi', 'Spa', 'Pool', 'Restaurant', 'Business Center'],
            amenitiesAr: ['واي فاي', 'سبا', 'مسبح', 'مطعم', 'مركز أعمال'],
            amenitiesTr: ['WiFi', 'Spa', 'Havuz', 'Restoran', 'İş Merkezi'],
            priceFrom: 380,
            image: '/api/placeholder/400/300',
            gallery: ['/api/placeholder/400/300', '/api/placeholder/400/300'],
            contact: {
                phone: '+90 212 924 0200',
                email: 'istanbul@raffles.com',
                website: 'https://www.raffles.com/istanbul',
                address: 'Zorlu Center, Beşiktaş, İstanbul',
                addressAr: 'مركز زورلو، بشيكتاش، إسطنبول',
                addressTr: 'Zorlu Center, Beşiktaş, İstanbul'
            },
            partnership: {
                discount: 12,
                specialRates: true,
                exclusiveAccess: false
            }
        },
        {
            id: '3',
            name: 'CVK Park Bosphorus',
            nameAr: 'فندق سي في كي بارك البوسفور',
            nameTr: 'CVK Park Bosphorus',
            location: 'Istanbul, Beşiktaş',
            locationAr: 'إسطنبول، بشيكتاش',
            locationTr: 'İstanbul, Beşiktaş',
            stars: 5,
            category: 'luxury',
            categoryAr: 'فاخر',
            categoryTr: 'Lüks',
            description: 'Elegant hotel with stunning Bosphorus views and exceptional service.',
            descriptionAr: 'فندق أنيق مع إطلالات خلابة على البوسفور وخدمة استثنائية.',
            descriptionTr: 'Muhteşem Boğaz manzarası ve olağanüstü hizmeti olan zarif otel.',
            amenities: ['WiFi', 'Spa', 'Pool', 'Restaurant', 'Fitness Center'],
            amenitiesAr: ['واي فاي', 'سبا', 'مسبح', 'مطعم', 'مركز لياقة'],
            amenitiesTr: ['WiFi', 'Spa', 'Havuz', 'Restoran', 'Fitness Merkezi'],
            priceFrom: 320,
            image: '/api/placeholder/400/300',
            gallery: ['/api/placeholder/400/300', '/api/placeholder/400/300'],
            contact: {
                phone: '+90 212 227 4400',
                email: 'info@cvkparkbosphorus.com',
                website: 'https://www.cvkparkbosphorus.com',
                address: 'Gümüşsuyu Mahallesi, İnönü Caddesi No:8, Beşiktaş/İstanbul',
                addressAr: 'حي جمشسوي، شارع إينونو رقم 8، بشيكتاش/إسطنبول',
                addressTr: 'Gümüşsuyu Mahallesi, İnönü Caddesi No:8, Beşiktaş/İstanbul'
            },
            partnership: {
                discount: 10,
                specialRates: true,
                exclusiveAccess: false
            }
        },
        {
            id: '4',
            name: 'Marriott Hotel Istanbul Sisli',
            nameAr: 'فندق ماريوت إسطنبول شيشلي',
            nameTr: 'İstanbul Şişli Marriott Hotel',
            location: 'Istanbul, Şişli',
            locationAr: 'إسطنبول، شيشلي',
            locationTr: 'İstanbul, Şişli',
            stars: 5,
            category: 'business',
            categoryAr: 'أعمال',
            categoryTr: 'İş',
            description: 'Modern business hotel in the heart of Istanbul\'s business district.',
            descriptionAr: 'فندق أعمال حديث في قلب منطقة الأعمال في إسطنبول.',
            descriptionTr: 'İstanbul\'un iş merkezinin kalbinde modern iş oteli.',
            amenities: ['WiFi', 'Business Center', 'Restaurant', 'Fitness Center', 'Meeting Rooms'],
            amenitiesAr: ['واي فاي', 'مركز أعمال', 'مطعم', 'مركز لياقة', 'قاعات اجتماعات'],
            amenitiesTr: ['WiFi', 'İş Merkezi', 'Restoran', 'Fitness Merkezi', 'Toplantı Salonları'],
            priceFrom: 280,
            image: '/api/placeholder/400/300',
            gallery: ['/api/placeholder/400/300', '/api/placeholder/400/300'],
            contact: {
                phone: '+90 212 315 6000',
                email: 'istanbul.sisli@marriott.com',
                website: 'https://www.marriott.com/istanbul-sisli',
                address: 'Abide-i Hürriyet Caddesi No:142, Şişli/İstanbul',
                addressAr: 'شارع عابدة الحرية رقم 142، شيشلي/إسطنبول',
                addressTr: 'Abide-i Hürriyet Caddesi No:142, Şişli/İstanbul'
            },
            partnership: {
                discount: 8,
                specialRates: true,
                exclusiveAccess: false
            }
        },
        {
            id: '5',
            name: 'Rixos Tersane Istanbul',
            nameAr: 'فندق ريكسوس ترسانة إسطنبول',
            nameTr: 'Rixos Tersane İstanbul',
            location: 'Istanbul, Karaköy',
            locationAr: 'إسطنبول، قره كوي',
            locationTr: 'İstanbul, Karaköy',
            stars: 5,
            category: 'resort',
            categoryAr: 'منتجع',
            categoryTr: 'Resort',
            description: 'Luxury resort with stunning Golden Horn views and world-class amenities.',
            descriptionAr: 'منتجع فاخر مع إطلالات خلابة على القرن الذهبي ومرافق عالمية المستوى.',
            descriptionTr: 'Muhteşem Haliç manzarası ve dünya standartlarında olanakları olan lüks resort.',
            amenities: ['WiFi', 'Spa', 'Pool', 'Restaurant', 'Beach Access'],
            amenitiesAr: ['واي فاي', 'سبا', 'مسبح', 'مطعم', 'وصول للشاطئ'],
            amenitiesTr: ['WiFi', 'Spa', 'Havuz', 'Restoran', 'Plaj Erişimi'],
            priceFrom: 400,
            image: '/api/placeholder/400/300',
            gallery: ['/api/placeholder/400/300', '/api/placeholder/400/300'],
            contact: {
                phone: '+90 212 244 0000',
                email: 'info@rixostersane.com',
                website: 'https://www.rixos.com/tersane-istanbul',
                address: 'Tersane Caddesi No:5, Karaköy/İstanbul',
                addressAr: 'شارع ترسانة رقم 5، قره كوي/إسطنبول',
                addressTr: 'Tersane Caddesi No:5, Karaköy/İstanbul'
            },
            partnership: {
                discount: 15,
                specialRates: true,
                exclusiveAccess: true
            }
        },
        {
            id: '6',
            name: 'Park Hyatt Istanbul - Maçka Palas',
            nameAr: 'فندق بارك حياة إسطنبول - معكا بالاس',
            nameTr: 'Park Hyatt İstanbul - Maçka Palas',
            location: 'Istanbul, Beşiktaş',
            locationAr: 'إسطنبول، بشيكتاش',
            locationTr: 'İstanbul, Beşiktaş',
            stars: 5,
            category: 'luxury',
            categoryAr: 'فاخر',
            categoryTr: 'Lüks',
            description: 'Historic luxury hotel with modern amenities and exceptional service.',
            descriptionAr: 'فندق فاخر تاريخي مع مرافق حديثة وخدمة استثنائية.',
            descriptionTr: 'Modern olanakları ve olağanüstü hizmeti olan tarihi lüks otel.',
            amenities: ['WiFi', 'Spa', 'Pool', 'Restaurant', 'Concierge'],
            amenitiesAr: ['واي فاي', 'سبا', 'مسبح', 'مطعم', 'خدمة الكونسيرج'],
            amenitiesTr: ['WiFi', 'Spa', 'Havuz', 'Restoran', 'Concierge'],
            priceFrom: 420,
            image: '/api/placeholder/400/300',
            gallery: ['/api/placeholder/400/300', '/api/placeholder/400/300'],
            contact: {
                phone: '+90 212 315 6000',
                email: 'istanbul.park@hyatt.com',
                website: 'https://www.hyatt.com/istanbul-park',
                address: 'Bronz Sokak No:4, Maçka/İstanbul',
                addressAr: 'شارع برونز رقم 4، معكا/إسطنبول',
                addressTr: 'Bronz Sokak No:4, Maçka/İstanbul'
            },
            partnership: {
                discount: 12,
                specialRates: true,
                exclusiveAccess: false
            }
        },
        {
            id: '7',
            name: 'Shangri-La Bosphorus Istanbul',
            nameAr: 'فندق شانغريلا البوسفور إسطنبول',
            nameTr: 'Shangri-La Bosphorus İstanbul',
            location: 'Istanbul, Beşiktaş',
            locationAr: 'إسطنبول، بشيكتاش',
            locationTr: 'İstanbul, Beşiktaş',
            stars: 5,
            category: 'luxury',
            categoryAr: 'فاخر',
            categoryTr: 'Lüks',
            description: 'Luxury hotel with stunning Bosphorus views and world-class dining.',
            descriptionAr: 'فندق فاخر مع إطلالات خلابة على البوسفور ومطاعم عالمية المستوى.',
            descriptionTr: 'Muhteşem Boğaz manzarası ve dünya standartlarında yemekleri olan lüks otel.',
            amenities: ['WiFi', 'Spa', 'Pool', 'Restaurant', 'Fitness Center'],
            amenitiesAr: ['واي فاي', 'سبا', 'مسبح', 'مطعم', 'مركز لياقة'],
            amenitiesTr: ['WiFi', 'Spa', 'Havuz', 'Restoran', 'Fitness Merkezi'],
            priceFrom: 480,
            image: '/api/placeholder/400/300',
            gallery: ['/api/placeholder/400/300', '/api/placeholder/400/300'],
            contact: {
                phone: '+90 212 275 8888',
                email: 'sli@shangri-la.com',
                website: 'https://www.shangri-la.com/istanbul',
                address: 'Sinanpaşa Mahallesi, Hayrettin İskelesi Sokak No:1, Beşiktaş/İstanbul',
                addressAr: 'حي سنان باشا، شارع خير الدين إسكله رقم 1، بشيكتاش/إسطنبول',
                addressTr: 'Sinanpaşa Mahallesi, Hayrettin İskelesi Sokak No:1, Beşiktaş/İstanbul'
            },
            partnership: {
                discount: 10,
                specialRates: true,
                exclusiveAccess: false
            }
        },
        {
            id: '8',
            name: 'The Peninsula Istanbul',
            nameAr: 'فندق البنسولا إسطنبول',
            nameTr: 'The Peninsula İstanbul',
            location: 'Istanbul, Karaköy',
            locationAr: 'إسطنبول، قره كوي',
            locationTr: 'İstanbul, Karaköy',
            stars: 5,
            category: 'luxury',
            categoryAr: 'فاخر',
            categoryTr: 'Lüks',
            description: 'Iconic luxury hotel with panoramic views and exceptional service.',
            descriptionAr: 'فندق فاخر أيقوني مع إطلالات بانورامية وخدمة استثنائية.',
            descriptionTr: 'Panoramik manzarası ve olağanüstü hizmeti olan ikonik lüks otel.',
            amenities: ['WiFi', 'Spa', 'Pool', 'Restaurant', 'Concierge'],
            amenitiesAr: ['واي فاي', 'سبا', 'مسبح', 'مطعم', 'خدمة الكونسيرج'],
            amenitiesTr: ['WiFi', 'Spa', 'Havuz', 'Restoran', 'Concierge'],
            priceFrom: 520,
            image: '/api/placeholder/400/300',
            gallery: ['/api/placeholder/400/300', '/api/placeholder/400/300'],
            contact: {
                phone: '+90 212 244 0000',
                email: 'istanbul@peninsula.com',
                website: 'https://www.peninsula.com/istanbul',
                address: 'Kemankeş Karamustafa Paşa Mahallesi, Kemankeş Caddesi No:34, Karaköy/İstanbul',
                addressAr: 'حي كمانكش كرامستافا باشا، شارع كمانكش رقم 34، قره كوي/إسطنبول',
                addressTr: 'Kemankeş Karamustafa Paşa Mahallesi, Kemankeş Caddesi No:34, Karaköy/İstanbul'
            },
            partnership: {
                discount: 15,
                specialRates: true,
                exclusiveAccess: true
            }
        },
        {
            id: '9',
            name: 'Hyatt Regency Istanbul Ataköy',
            nameAr: 'فندق حياة ريجنسي إسطنبول أتاكوي',
            nameTr: 'Hyatt Regency İstanbul Ataköy',
            location: 'Istanbul, Ataköy',
            locationAr: 'إسطنبول، أتاكوي',
            locationTr: 'İstanbul, Ataköy',
            stars: 5,
            category: 'business',
            categoryAr: 'أعمال',
            categoryTr: 'İş',
            description: 'Modern business hotel with excellent facilities and service.',
            descriptionAr: 'فندق أعمال حديث مع مرافق ممتازة وخدمة رائعة.',
            descriptionTr: 'Mükemmel olanakları ve hizmeti olan modern iş oteli.',
            amenities: ['WiFi', 'Business Center', 'Restaurant', 'Fitness Center', 'Meeting Rooms'],
            amenitiesAr: ['واي فاي', 'مركز أعمال', 'مطعم', 'مركز لياقة', 'قاعات اجتماعات'],
            amenitiesTr: ['WiFi', 'İş Merkezi', 'Restoran', 'Fitness Merkezi', 'Toplantı Salonları'],
            priceFrom: 300,
            image: '/api/placeholder/400/300',
            gallery: ['/api/placeholder/400/300', '/api/placeholder/400/300'],
            contact: {
                phone: '+90 212 560 7000',
                email: 'istanbul.regency@hyatt.com',
                website: 'https://www.hyatt.com/istanbul-regency',
                address: 'Ataköy 7-8-9-10. Kısım Mahallesi, Yeşilköy Halkalı Caddesi No:1, Bakırköy/İstanbul',
                addressAr: 'حي أتاكوي 7-8-9-10، شارع يشيلكوي هالكالي رقم 1، باقر كوي/إسطنبول',
                addressTr: 'Ataköy 7-8-9-10. Kısım Mahallesi, Yeşilköy Halkalı Caddesi No:1, Bakırköy/İstanbul'
            },
            partnership: {
                discount: 8,
                specialRates: true,
                exclusiveAccess: false
            }
        },
        {
            id: '10',
            name: 'JW Marriott Hotel Istanbul Marmara Sea',
            nameAr: 'فندق جي دبليو ماريوت إسطنبول بحر مرمرة',
            nameTr: 'JW Marriott Hotel İstanbul Marmara Denizi',
            location: 'Istanbul, Kadıköy',
            locationAr: 'إسطنبول، كاديكوي',
            locationTr: 'İstanbul, Kadıköy',
            stars: 5,
            category: 'luxury',
            categoryAr: 'فاخر',
            categoryTr: 'Lüks',
            description: 'Luxury hotel with stunning Marmara Sea views and exceptional amenities.',
            descriptionAr: 'فندق فاخر مع إطلالات خلابة على بحر مرمرة ومرافق استثنائية.',
            descriptionTr: 'Muhteşem Marmara Denizi manzarası ve olağanüstü olanakları olan lüks otel.',
            amenities: ['WiFi', 'Spa', 'Pool', 'Restaurant', 'Fitness Center'],
            amenitiesAr: ['واي فاي', 'سبا', 'مسبح', 'مطعم', 'مركز لياقة'],
            amenitiesTr: ['WiFi', 'Spa', 'Havuz', 'Restoran', 'Fitness Merkezi'],
            priceFrom: 350,
            image: '/api/placeholder/400/300',
            gallery: ['/api/placeholder/400/300', '/api/placeholder/400/300'],
            contact: {
                phone: '+90 216 315 3000',
                email: 'istanbul.jw@marriott.com',
                website: 'https://www.marriott.com/istanbul-jw',
                address: 'Kadıköy Mahallesi, Moda Caddesi No:15, Kadıköy/İstanbul',
                addressAr: 'حي كاديكوي، شارع مودا رقم 15، كاديكوي/إسطنبول',
                addressTr: 'Kadıköy Mahallesi, Moda Caddesi No:15, Kadıköy/İstanbul'
            },
            partnership: {
                discount: 10,
                specialRates: true,
                exclusiveAccess: false
            }
        },
        {
            id: '11',
            name: 'Kaya Palazzo Resort & Residences Le Chic Bodrum',
            nameAr: 'منتجع كايا بالازو ومساكن لو شيك بودروم',
            nameTr: 'Kaya Palazzo Resort & Residences Le Chic Bodrum',
            location: 'Bodrum, Muğla',
            locationAr: 'بودروم، موغلا',
            locationTr: 'Bodrum, Muğla',
            stars: 5,
            category: 'resort',
            categoryAr: 'منتجع',
            categoryTr: 'Resort',
            description: 'Luxury beachfront resort with stunning Aegean Sea views and world-class amenities.',
            descriptionAr: 'منتجع فاخر على الشاطئ مع إطلالات خلابة على بحر إيجة ومرافق عالمية المستوى.',
            descriptionTr: 'Muhteşem Ege Denizi manzarası ve dünya standartlarında olanakları olan lüks sahil resort.',
            amenities: ['WiFi', 'Spa', 'Pool', 'Beach Access', 'Restaurant'],
            amenitiesAr: ['واي فاي', 'سبا', 'مسبح', 'وصول للشاطئ', 'مطعم'],
            amenitiesTr: ['WiFi', 'Spa', 'Havuz', 'Plaj Erişimi', 'Restoran'],
            priceFrom: 450,
            image: '/api/placeholder/400/300',
            gallery: ['/api/placeholder/400/300', '/api/placeholder/400/300'],
            contact: {
                phone: '+90 252 311 1888',
                email: 'info@kayapalazzo.com',
                website: 'https://www.kayapalazzo.com/bodrum',
                address: 'Gümbet Mahallesi, Atatürk Caddesi No:118, Bodrum/Muğla',
                addressAr: 'حي كومبت، شارع أتاتورك رقم 118، بودروم/موغلا',
                addressTr: 'Gümbet Mahallesi, Atatürk Caddesi No:118, Bodrum/Muğla'
            },
            partnership: {
                discount: 12,
                specialRates: true,
                exclusiveAccess: false
            }
        },
        {
            id: '12',
            name: 'Vogue Hotel Supreme Bodrum',
            nameAr: 'فندق فوغ سوبريم بودروم',
            nameTr: 'Vogue Hotel Supreme Bodrum',
            location: 'Bodrum, Muğla',
            locationAr: 'بودروم، موغلا',
            locationTr: 'Bodrum, Muğla',
            stars: 5,
            category: 'luxury',
            categoryAr: 'فاخر',
            categoryTr: 'Lüks',
            description: 'Exclusive luxury hotel with private beach and exceptional service.',
            descriptionAr: 'فندق فاخر حصري مع شاطئ خاص وخدمة استثنائية.',
            descriptionTr: 'Özel plajı ve olağanüstü hizmeti olan özel lüks otel.',
            amenities: ['WiFi', 'Spa', 'Pool', 'Private Beach', 'Restaurant'],
            amenitiesAr: ['واي فاي', 'سبا', 'مسبح', 'شاطئ خاص', 'مطعم'],
            amenitiesTr: ['WiFi', 'Spa', 'Havuz', 'Özel Plaj', 'Restoran'],
            priceFrom: 380,
            image: '/api/placeholder/400/300',
            gallery: ['/api/placeholder/400/300', '/api/placeholder/400/300'],
            contact: {
                phone: '+90 252 311 1888',
                email: 'info@voguehotel.com',
                website: 'https://www.voguehotel.com/bodrum',
                address: 'Gümbet Mahallesi, Atatürk Caddesi No:120, Bodrum/Muğla',
                addressAr: 'حي كومبت، شارع أتاتورك رقم 120، بودروم/موغلا',
                addressTr: 'Gümbet Mahallesi, Atatürk Caddesi No:120, Bodrum/Muğla'
            },
            partnership: {
                discount: 10,
                specialRates: true,
                exclusiveAccess: false
            }
        },
        {
            id: '13',
            name: 'Mövenpick Hotel Trabzon',
            nameAr: 'فندق موفنبيك طرابزون',
            nameTr: 'Mövenpick Hotel Trabzon',
            location: 'Trabzon',
            locationAr: 'طرابزون',
            locationTr: 'Trabzon',
            stars: 5,
            category: 'business',
            categoryAr: 'أعمال',
            categoryTr: 'İş',
            description: 'Modern business hotel in the heart of Trabzon with excellent facilities.',
            descriptionAr: 'فندق أعمال حديث في قلب طرابزون مع مرافق ممتازة.',
            descriptionTr: 'Trabzon\'un kalbinde mükemmel olanakları olan modern iş oteli.',
            amenities: ['WiFi', 'Business Center', 'Restaurant', 'Fitness Center', 'Meeting Rooms'],
            amenitiesAr: ['واي فاي', 'مركز أعمال', 'مطعم', 'مركز لياقة', 'قاعات اجتماعات'],
            amenitiesTr: ['WiFi', 'İş Merkezi', 'Restoran', 'Fitness Merkezi', 'Toplantı Salonları'],
            priceFrom: 250,
            image: '/api/placeholder/400/300',
            gallery: ['/api/placeholder/400/300', '/api/placeholder/400/300'],
            contact: {
                phone: '+90 462 455 0000',
                email: 'trabzon@moevenpick.com',
                website: 'https://www.moevenpick.com/trabzon',
                address: 'Ortahisar Mahallesi, Atatürk Alanı No:1, Trabzon',
                addressAr: 'حي أورتا حصار، ساحة أتاتورك رقم 1، طرابزون',
                addressTr: 'Ortahisar Mahallesi, Atatürk Alanı No:1, Trabzon'
            },
            partnership: {
                discount: 8,
                specialRates: true,
                exclusiveAccess: false
            }
        },
        {
            id: '14',
            name: 'Residence Inn Trabzon',
            nameAr: 'ريزيدنس إن طرابزون',
            nameTr: 'Residence Inn Trabzon',
            location: 'Trabzon',
            locationAr: 'طرابزون',
            locationTr: 'Trabzon',
            stars: 4,
            category: 'apartment',
            categoryAr: 'شقة',
            categoryTr: 'Apartman',
            description: 'Extended stay hotel with spacious suites and modern amenities.',
            descriptionAr: 'فندق إقامة طويلة مع جناحات واسعة ومرافق حديثة.',
            descriptionTr: 'Geniş süitleri ve modern olanakları olan uzun süreli konaklama oteli.',
            amenities: ['WiFi', 'Kitchenette', 'Restaurant', 'Fitness Center', 'Laundry'],
            amenitiesAr: ['واي فاي', 'مطبخ صغير', 'مطعم', 'مركز لياقة', 'غسيل'],
            amenitiesTr: ['WiFi', 'Mini Mutfak', 'Restoran', 'Fitness Merkezi', 'Çamaşır'],
            priceFrom: 180,
            image: '/api/placeholder/400/300',
            gallery: ['/api/placeholder/400/300', '/api/placeholder/400/300'],
            contact: {
                phone: '+90 462 455 0000',
                email: 'trabzon@residenceinn.com',
                website: 'https://www.marriott.com/trabzon-residence',
                address: 'Ortahisar Mahallesi, Atatürk Alanı No:3, Trabzon',
                addressAr: 'حي أورتا حصار، ساحة أتاتورك رقم 3، طرابزون',
                addressTr: 'Ortahisar Mahallesi, Atatürk Alanı No:3, Trabzon'
            },
            partnership: {
                discount: 6,
                specialRates: true,
                exclusiveAccess: false
            }
        },
        {
            id: '15',
            name: 'Elite World Grand Sapanca',
            nameAr: 'إيليت وورلد جراند صابانجا',
            nameTr: 'Elite World Grand Sapanca',
            location: 'Sapanca, Sakarya',
            locationAr: 'صابانجا، ساكاريا',
            locationTr: 'Sapanca, Sakarya',
            stars: 5,
            category: 'resort',
            categoryAr: 'منتجع',
            categoryTr: 'Resort',
            description: 'Luxury resort with stunning lake views and world-class amenities.',
            descriptionAr: 'منتجع فاخر مع إطلالات خلابة على البحيرة ومرافق عالمية المستوى.',
            descriptionTr: 'Muhteşem göl manzarası ve dünya standartlarında olanakları olan lüks resort.',
            amenities: ['WiFi', 'Spa', 'Pool', 'Lake Access', 'Restaurant'],
            amenitiesAr: ['واي فاي', 'سبا', 'مسبح', 'وصول للبحيرة', 'مطعم'],
            amenitiesTr: ['WiFi', 'Spa', 'Havuz', 'Göl Erişimi', 'Restoran'],
            priceFrom: 320,
            image: '/api/placeholder/400/300',
            gallery: ['/api/placeholder/400/300', '/api/placeholder/400/300'],
            contact: {
                phone: '+90 264 582 0000',
                email: 'info@eliteworldsapanca.com',
                website: 'https://www.eliteworldsapanca.com',
                address: 'Sapanca Mahallesi, Atatürk Caddesi No:1, Sapanca/Sakarya',
                addressAr: 'حي صابانجا، شارع أتاتورك رقم 1، صابانجا/ساكاريا',
                addressTr: 'Sapanca Mahallesi, Atatürk Caddesi No:1, Sapanca/Sakarya'
            },
            partnership: {
                discount: 10,
                specialRates: true,
                exclusiveAccess: false
            }
        },
        {
            id: '16',
            name: 'Ramada Resort By Wyndham Thermal Sapanca',
            nameAr: 'منتجع رامادا بويندهام ثيرمال صابانجا',
            nameTr: 'Ramada Resort By Wyndham Thermal Sapanca',
            location: 'Sapanca, Sakarya',
            locationAr: 'صابانجا، ساكاريا',
            locationTr: 'Sapanca, Sakarya',
            stars: 5,
            category: 'resort',
            categoryAr: 'منتجع',
            categoryTr: 'Resort',
            description: 'Thermal resort with natural hot springs and wellness facilities.',
            descriptionAr: 'منتجع حراري مع ينابيع ساخنة طبيعية ومرافق صحية.',
            descriptionTr: 'Doğal kaplıcaları ve wellness olanakları olan termal resort.',
            amenities: ['WiFi', 'Thermal Spa', 'Pool', 'Wellness Center', 'Restaurant'],
            amenitiesAr: ['واي فاي', 'سبا حراري', 'مسبح', 'مركز صحي', 'مطعم'],
            amenitiesTr: ['WiFi', 'Termal Spa', 'Havuz', 'Wellness Merkezi', 'Restoran'],
            priceFrom: 280,
            image: '/api/placeholder/400/300',
            gallery: ['/api/placeholder/400/300', '/api/placeholder/400/300'],
            contact: {
                phone: '+90 264 582 0000',
                email: 'info@ramadasapanca.com',
                website: 'https://www.ramada.com/sapanca',
                address: 'Sapanca Mahallesi, Atatürk Caddesi No:5, Sapanca/Sakarya',
                addressAr: 'حي صابانجا، شارع أتاتورك رقم 5، صابانجا/ساكاريا',
                addressTr: 'Sapanca Mahallesi, Atatürk Caddesi No:5, Sapanca/Sakarya'
            },
            partnership: {
                discount: 8,
                specialRates: true,
                exclusiveAccess: false
            }
        },
        {
            id: '17',
            name: 'Radisson Collection Hotel, VadiIstanbul',
            nameAr: 'فندق راديسون كولكشن، وادي إسطنبول',
            nameTr: 'Radisson Collection Hotel, Vadiİstanbul',
            location: 'Istanbul, Sarıyer',
            locationAr: 'إسطنبول، صاريير',
            locationTr: 'İstanbul, Sarıyer',
            stars: 5,
            category: 'luxury',
            categoryAr: 'فاخر',
            categoryTr: 'Lüks',
            description: 'Modern luxury hotel with stunning city views and exceptional amenities.',
            descriptionAr: 'فندق فاخر حديث مع إطلالات خلابة على المدينة ومرافق استثنائية.',
            descriptionTr: 'Muhteşem şehir manzarası ve olağanüstü olanakları olan modern lüks otel.',
            amenities: ['WiFi', 'Spa', 'Pool', 'Restaurant', 'Fitness Center'],
            amenitiesAr: ['واي فاي', 'سبا', 'مسبح', 'مطعم', 'مركز لياقة'],
            amenitiesTr: ['WiFi', 'Spa', 'Havuz', 'Restoran', 'Fitness Merkezi'],
            priceFrom: 350,
            image: '/api/placeholder/400/300',
            gallery: ['/api/placeholder/400/300', '/api/placeholder/400/300'],
            contact: {
                phone: '+90 212 315 6000',
                email: 'info@radissonvadi.com',
                website: 'https://www.radissonhotels.com/vadi-istanbul',
                address: 'VadiIstanbul Mahallesi, Büyükdere Caddesi No:1, Sarıyer/İstanbul',
                addressAr: 'حي وادي إسطنبول، شارع بويوكديري رقم 1، صاريير/إسطنبول',
                addressTr: 'Vadiİstanbul Mahallesi, Büyükdere Caddesi No:1, Sarıyer/İstanbul'
            },
            partnership: {
                discount: 10,
                specialRates: true,
                exclusiveAccess: false
            }
        },
        {
            id: '18',
            name: 'Wyndham Grand Istanbul Levent',
            nameAr: 'فندق ويندهام جراند إسطنبول ليفنت',
            nameTr: 'Wyndham Grand İstanbul Levent',
            location: 'Istanbul, Levent',
            locationAr: 'إسطنبول، ليفنت',
            locationTr: 'İstanbul, Levent',
            stars: 5,
            category: 'business',
            categoryAr: 'أعمال',
            categoryTr: 'İş',
            description: 'Premium business hotel in the heart of Istanbul\'s financial district.',
            descriptionAr: 'فندق أعمال متميز في قلب المنطقة المالية في إسطنبول.',
            descriptionTr: 'İstanbul\'un finans merkezinin kalbinde premium iş oteli.',
            amenities: ['WiFi', 'Business Center', 'Restaurant', 'Fitness Center', 'Meeting Rooms'],
            amenitiesAr: ['واي فاي', 'مركز أعمال', 'مطعم', 'مركز لياقة', 'قاعات اجتماعات'],
            amenitiesTr: ['WiFi', 'İş Merkezi', 'Restoran', 'Fitness Merkezi', 'Toplantı Salonları'],
            priceFrom: 320,
            image: '/api/placeholder/400/300',
            gallery: ['/api/placeholder/400/300', '/api/placeholder/400/300'],
            contact: {
                phone: '+90 212 315 6000',
                email: 'info@wyndhamlevent.com',
                website: 'https://www.wyndhamhotels.com/istanbul-levent',
                address: 'Levent Mahallesi, Büyükdere Caddesi No:1, Levent/İstanbul',
                addressAr: 'حي ليفنت، شارع بويوكديري رقم 1، ليفنت/إسطنبول',
                addressTr: 'Levent Mahallesi, Büyükdere Caddesi No:1, Levent/İstanbul'
            },
            partnership: {
                discount: 8,
                specialRates: true,
                exclusiveAccess: false
            }
        },
        {
            id: '19',
            name: 'The Westin Istanbul Nisantasi',
            nameAr: 'فندق ويستن إسطنبول نيشانتاشي',
            nameTr: 'The Westin İstanbul Nişantaşı',
            location: 'Istanbul, Nişantaşı',
            locationAr: 'إسطنبول، نيشانتاشي',
            locationTr: 'İstanbul, Nişantaşı',
            stars: 5,
            category: 'luxury',
            categoryAr: 'فاخر',
            categoryTr: 'Lüks',
            description: 'Luxury hotel in the fashionable Nişantaşı district with world-class amenities.',
            descriptionAr: 'فندق فاخر في حي نيشانتاشي الأنيق مع مرافق عالمية المستوى.',
            descriptionTr: 'Şık Nişantaşı bölgesinde dünya standartlarında olanakları olan lüks otel.',
            amenities: ['WiFi', 'Spa', 'Pool', 'Restaurant', 'Fitness Center'],
            amenitiesAr: ['واي فاي', 'سبا', 'مسبح', 'مطعم', 'مركز لياقة'],
            amenitiesTr: ['WiFi', 'Spa', 'Havuz', 'Restoran', 'Fitness Merkezi'],
            priceFrom: 400,
            image: '/api/placeholder/400/300',
            gallery: ['/api/placeholder/400/300', '/api/placeholder/400/300'],
            contact: {
                phone: '+90 212 315 6000',
                email: 'info@westinnisantasi.com',
                website: 'https://www.marriott.com/istanbul-westin',
                address: 'Nişantaşı Mahallesi, Abdi İpekçi Caddesi No:1, Nişantaşı/İstanbul',
                addressAr: 'حي نيشانتاشي، شارع عبدي إيبكجي رقم 1، نيشانتاشي/إسطنبول',
                addressTr: 'Nişantaşı Mahallesi, Abdi İpekçi Caddesi No:1, Nişantaşı/İstanbul'
            },
            partnership: {
                discount: 12,
                specialRates: true,
                exclusiveAccess: false
            }
        },
        {
            id: '20',
            name: 'Sanasaryan Han, A Luxury Collection Hotel',
            nameAr: 'فندق صنعساريان خان، مجموعة فاخرة',
            nameTr: 'Sanasaryan Han, A Luxury Collection Hotel',
            location: 'Istanbul, Beyoğlu',
            locationAr: 'إسطنبول، بيوغلو',
            locationTr: 'İstanbul, Beyoğlu',
            stars: 5,
            category: 'luxury',
            categoryAr: 'فاخر',
            categoryTr: 'Lüks',
            description: 'Historic luxury hotel with modern amenities and exceptional service.',
            descriptionAr: 'فندق فاخر تاريخي مع مرافق حديثة وخدمة استثنائية.',
            descriptionTr: 'Modern olanakları ve olağanüstü hizmeti olan tarihi lüks otel.',
            amenities: ['WiFi', 'Spa', 'Restaurant', 'Concierge', 'Historic Building'],
            amenitiesAr: ['واي فاي', 'سبا', 'مطعم', 'خدمة الكونسيرج', 'مبنى تاريخي'],
            amenitiesTr: ['WiFi', 'Spa', 'Restoran', 'Concierge', 'Tarihi Bina'],
            priceFrom: 450,
            image: '/api/placeholder/400/300',
            gallery: ['/api/placeholder/400/300', '/api/placeholder/400/300'],
            contact: {
                phone: '+90 212 315 6000',
                email: 'info@sanasaryanhan.com',
                website: 'https://www.marriott.com/istanbul-sanasaryan',
                address: 'Beyoğlu Mahallesi, İstiklal Caddesi No:1, Beyoğlu/İstanbul',
                addressAr: 'حي بيوغلو، شارع الاستقلال رقم 1، بيوغلو/إسطنبول',
                addressTr: 'Beyoğlu Mahallesi, İstiklal Caddesi No:1, Beyoğlu/İstanbul'
            },
            partnership: {
                discount: 15,
                specialRates: true,
                exclusiveAccess: true
            }
        }
    ];

    const locations = ['Istanbul', 'Bodrum', 'Trabzon', 'Sapanca'];
    const categories = ['luxury', 'business', 'resort', 'boutique', 'apartment'];

    useEffect(() => {
        // Simulate loading
        setTimeout(() => {
            setHotels(mockHotels);
            setFilteredHotels(mockHotels);
            setIsLoading(false);
        }, 1000);
    }, []);

    useEffect(() => {
        let filtered = hotels;

        // Search filter
        if (searchTerm) {
            filtered = filtered.filter(hotel => 
                hotel.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                hotel.location.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Location filter
        if (selectedLocation) {
            filtered = filtered.filter(hotel => 
                hotel.location.includes(selectedLocation)
            );
        }

        // Stars filter
        if (selectedStars > 0) {
            filtered = filtered.filter(hotel => hotel.stars >= selectedStars);
        }

        // Category filter
        if (selectedCategory) {
            filtered = filtered.filter(hotel => hotel.category === selectedCategory);
        }

        // Sort
        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'name':
                    return a.name.localeCompare(b.name);
                case 'price':
                    return a.priceFrom - b.priceFrom;
                case 'stars':
                    return b.stars - a.stars;
                default:
                    return 0;
            }
        });

        setFilteredHotels(filtered);
    }, [hotels, searchTerm, selectedLocation, selectedStars, selectedCategory, sortBy]);

    const clearFilters = () => {
        setSearchTerm('');
        setSelectedLocation('');
        setSelectedStars(0);
        setSelectedCategory('');
        setSortBy('name');
    };

    const getCategoryColor = (category: string) => {
        switch (category) {
            case 'luxury': return '#8B5CF6';
            case 'business': return '#3B82F6';
            case 'resort': return '#10B981';
            case 'boutique': return '#F59E0B';
            case 'apartment': return '#EF4444';
            default: return '#6B7280';
        }
    };

    if (isLoading) {
        return (
            <div className="partner-hotels-page">
                <div className="container">
                    <div className="loading-spinner">
                        <div className="spinner"></div>
                        <p>{t('common.loading')}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="partner-hotels-page">
            <div className="container">
                {/* Hero Section */}
                <motion.div
                    className="partner-hotels-hero"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    <h1>{t('partnerHotels.title')}</h1>
                    <p>{t('partnerHotels.subtitle')}</p>
                </motion.div>

                {/* Filters Section */}
                <motion.div
                    className="partner-hotels-filters"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.6 }}
                >
                    <div className="filters-header">
                        <h3>{t('partnerHotels.filters')}</h3>
                        <button onClick={clearFilters} className="btn btn-secondary">
                            {t('partnerHotels.clearFilters')}
                        </button>
                    </div>

                    <div className="filters-grid">
                        <div className="filter-group">
                            <label>{t('partnerHotels.search')}</label>
                            <input
                                type="text"
                                placeholder={t('partnerHotels.searchPlaceholder')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="input"
                            />
                        </div>

                        <div className="filter-group">
                            <label>{t('partnerHotels.location')}</label>
                            <select
                                value={selectedLocation}
                                onChange={(e) => setSelectedLocation(e.target.value)}
                                className="input"
                            >
                                <option value="">{t('partnerHotels.allLocations')}</option>
                                {locations.map(location => (
                                    <option key={location} value={location}>{location}</option>
                                ))}
                            </select>
                        </div>

                        <div className="filter-group">
                            <label>{t('partnerHotels.stars')}</label>
                            <div className="stars-filter">
                                {[5, 4, 3].map(stars => (
                                    <button
                                        key={stars}
                                        className={`star-btn ${selectedStars === stars ? 'active' : ''}`}
                                        onClick={() => setSelectedStars(selectedStars === stars ? 0 : stars)}
                                    >
                                        {'★'.repeat(stars)}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="filter-group">
                            <label>{t('partnerHotels.category')}</label>
                            <select
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                                className="input"
                            >
                                <option value="">{t('partnerHotels.allCategories')}</option>
                                {categories.map(category => (
                                    <option key={category} value={category}>
                                        {t(`partnerHotels.categories.${category}`)}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="filter-group">
                            <label>{t('partnerHotels.sortBy')}</label>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="input"
                            >
                                <option value="name">{t('partnerHotels.sortName')}</option>
                                <option value="price">{t('partnerHotels.sortPrice')}</option>
                                <option value="stars">{t('partnerHotels.sortStars')}</option>
                            </select>
                        </div>
                    </div>
                </motion.div>

                {/* Results Section */}
                <motion.div
                    className="partner-hotels-results"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.6 }}
                >
                    <div className="results-header">
                        <h3>{t('partnerHotels.results')}</h3>
                        <p>{filteredHotels.length} {t('partnerHotels.hotelsFound')}</p>
                    </div>

                    {filteredHotels.length === 0 ? (
                        <div className="no-results">
                            <h4>{t('partnerHotels.noResultsTitle')}</h4>
                            <p>{t('partnerHotels.noResultsDesc')}</p>
                        </div>
                    ) : (
                        <div className="hotels-grid">
                            {filteredHotels.map((hotel, index) => (
                                <motion.div
                                    key={hotel.id}
                                    className="hotel-card"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.1, duration: 0.5 }}
                                    whileHover={{ y: -5 }}
                                >
                                    <div className="hotel-image">
                                        <img src={hotel.image} alt={hotel.name} />
                                        <div className="hotel-badges">
                                            <span 
                                                className="category-badge"
                                                style={{ backgroundColor: getCategoryColor(hotel.category) }}
                                            >
                                                {t(`partnerHotels.categories.${hotel.category}`)}
                                            </span>
                                            {hotel.partnership.exclusiveAccess && (
                                                <span className="exclusive-badge">
                                                    {t('partnerHotels.exclusive')}
                                                </span>
                                            )}
                                        </div>
                                        <div className="stars-display">
                                            {'★'.repeat(hotel.stars)}
                                        </div>
                                    </div>

                                    <div className="hotel-content">
                                        <h3>{hotel.name}</h3>
                                        <p className="hotel-location">{hotel.location}</p>
                                        <p className="hotel-description">{hotel.description}</p>
                                        
                                        <div className="hotel-amenities">
                                            {hotel.amenities.slice(0, 3).map((amenity, idx) => (
                                                <span key={idx} className="amenity-tag">
                                                    {amenity}
                                                </span>
                                            ))}
                                        </div>

                                        <div className="hotel-footer">
                                            <div className="price-info">
                                                <span className="price-from">{t('partnerHotels.from')}</span>
                                                <span className="price">{formatPrice(hotel.priceFrom)}</span>
                                                {hotel.partnership.discount > 0 && (
                                                    <span className="discount">
                                                        {t('partnerHotels.discount')} {hotel.partnership.discount}%
                                                    </span>
                                                )}
                                            </div>
                                            <button className="btn btn-primary">
                                                {t('partnerHotels.viewDetails')}
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    )}
                </motion.div>
            </div>
        </div>
    );
};

export default PartnerHotels;
